import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;

export default function EmailVerificationModal({
  open,
  status,
  onClose,
  onVerified,
}) {
  const [step, setStep] = useState("email");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const [maskedEmail, setMaskedEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

    useEffect(() => {
  if (!open) return;

  setStep("email");

  setEmail(
    status?.verified
      ? ""
      : status?.email || ""
  );

  setCode("");
  setMaskedEmail("");
  setError("");
  setLoading(false);
}, [open]);

  if (!open) {
    return null;
  }

  const rewardCurrency =
    status?.reward?.currency || "sparks";

  const rewardAmount =
    Number(status?.reward?.amount || 0);

  const rewardIcon =
    rewardCurrency === "runes"
      ? "/runes.png"
      : "/sparks.png";

  const rewardLabel =
    rewardCurrency === "runes"
      ? "Runes"
      : "Sparks";

  const canReceiveReward =
    !status?.rewardAlreadyPaid &&
    rewardAmount > 0;

  function getHeaders() {
    const token =
      localStorage.getItem("token");

    return {
      "Content-Type": "application/json",

      Authorization:
        `Bearer ${token}`,

      "X-TG-INITDATA":
        window.Telegram?.WebApp?.initData ||
        "",
    };
  }

  function translateRequestError(data) {
    switch (data?.error) {
      case "EMAIL_ALREADY_IN_USE":
        return "This email is already being used by another account.";

      case "EMAIL_ALREADY_VERIFIED":
        return "This email is already verified.";

      case "WAIT_BEFORE_RESEND":
        return "Please wait before requesting another code.";

      case "INVALID_EMAIL":
        return "Enter a valid email address.";

      default:
        return (
          data?.error ||
          "Could not send verification code."
        );
    }
  }

  function translateVerifyError(data) {
    switch (data?.error) {
      case "WRONG_CODE":
        return `Wrong code. ${
          data?.attemptsRemaining ?? 0
        } attempts remaining.`;

      case "OTP_EXPIRED":
        return "This code has expired. Request a new one.";

      case "OTP_NOT_FOUND":
        return "No active verification code was found.";

      case "TOO_MANY_ATTEMPTS":
        return "Too many attempts. Request a new code.";

      case "EMAIL_ALREADY_IN_USE":
        return "This email is already being used by another account.";

      case "INVALID_CODE":
        return "Enter the 6-digit verification code.";

      case "INVALID_EMAIL":
        return "Invalid email address.";

      default:
        return (
          data?.error ||
          "Could not verify email."
        );
    }
  }

  async function sendCode() {
    if (loading) return;

    const cleanEmail =
      String(email || "")
        .trim()
        .toLowerCase();

    if (!cleanEmail) {
      setError(
        "Enter your email address."
      );

      return;
    }

    setLoading(true);
    setError("");

    try {
      const token =
        localStorage.getItem("token");

      if (!token) {
        throw new Error(
          "Missing authentication token."
        );
      }

      const res = await fetch(
        `${API_BASE}/api/email/request`,
        {
          method: "POST",

          headers:
            getHeaders(),

          body: JSON.stringify({
            email: cleanEmail,
          }),
        }
      );

      const data =
        await res
          .json()
          .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          translateRequestError(data)
        );
      }

      setEmail(cleanEmail);

      setMaskedEmail(
        data?.maskedEmail ||
        cleanEmail
      );

      setCode("");
      setStep("code");

    } catch (err) {
      console.error(
        "[EMAIL REQUEST ERROR]",
        err
      );

      setError(
        err?.message ||
        "Could not send verification code."
      );

    } finally {
      setLoading(false);
    }
  }

  async function verifyCode() {
    if (loading) return;

    const cleanCode =
      String(code || "")
        .trim();

    if (!/^\d{6}$/.test(cleanCode)) {
      setError(
        "Enter the 6-digit verification code."
      );

      return;
    }

    setLoading(true);
    setError("");

    try {
      const token =
        localStorage.getItem("token");

      if (!token) {
        throw new Error(
          "Missing authentication token."
        );
      }

      const res = await fetch(
        `${API_BASE}/api/email/verify`,
        {
          method: "POST",

          headers:
            getHeaders(),

          body: JSON.stringify({
            email,
            code: cleanCode,
          }),
        }
      );

      const data =
        await res
          .json()
          .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          translateVerifyError(data)
        );
      }

      /*
       * IMPORTANTE:
       *
       * Chamamos o ProfileModal primeiro
       * para ele atualizar:
       *
       * email
       * email_verified_at
       * sparks
       * runes
       */
      onVerified?.(data);

      /*
       * Depois mostramos sucesso.
       */
      setStep("success");
      setError("");

    } catch (err) {
      console.error(
        "[EMAIL VERIFY ERROR]",
        err
      );

      setError(
        err?.message ||
        "Could not verify email."
      );

    } finally {
      setLoading(false);
    }
  }

  function changeEmail() {
    if (loading) return;

    setStep("email");
    setCode("");
    setMaskedEmail("");
    setError("");
  }

  return (
  <div className="modal_box">
    <div className="modal_title">
      Verification

      <button
        type="button"
        className="modal_close"
        onClick={() => {
          if (!loading) {
            onClose?.();
          }
        }}
      />
    </div>

    <motion.div
      className="modal_body"
      initial={{
        scale: 0.8,
        opacity: 0,
      }}
      animate={{
        scale: 1,
        opacity: 1,
      }}
      exit={{
        scale: 0.92,
        opacity: 0,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}
    >
      <div
        className="modal_inside"
        style={{
          textAlign: "center",
        }}
      >
        {/* =========================================
            STEP 1 - EMAIL
            ========================================= */}

        {step === "email" && (
          <>
            <div
              className="shadow2"
              style={{
                color: "#fff",
                fontSize: 22,
                lineHeight: "27px",
                fontWeight: 900,
                marginBottom: 8,
              }}
            >
              Verify Your Email
            </div>

            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 14,
                lineHeight: "19px",
                fontWeight: 600,
                marginBottom: 15,
              }}
            >
              Enter your email address and we will send you a
              verification code.
            </div>

            {canReceiveReward && (
              <div
                className="info"
                style={{
                  marginBottom: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                <span
                  className="shadow1"
                  style={{
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 700,
                  }}
                >
                  Verify and earn
                </span>

                <img
                  src={rewardIcon}
                  alt=""
                  style={{
                    width: 27,
                    height: 27,
                    objectFit: "contain",
                  }}
                />

                <span
                  className="shadow2"
                  style={{
                    color: "#ffe16a",
                    fontSize: 19,
                    fontWeight: 900,
                  }}
                >
                  {rewardAmount} {rewardLabel}
                </span>
              </div>
            )}

            <input
              type="email"
              value={email}
              disabled={loading}
              autoComplete="email"
              placeholder="you@email.com"
              onChange={(e) => {
                setEmail(e.target.value);

                if (error) {
                  setError("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  sendCode();
                }
              }}
              style={{
                width: "calc(100% + 20px)",
                height: 48,
                boxSizing: "border-box",
                borderRadius: 7,
                border: "3px solid #5d2a13",
                background: "#2a1208",
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                padding: "0 12px",
                outline: "none",
                textAlign: "center",
                marginLeft: -10,
                marginRight: -10,
                marginBottom: -10
              }}
            />

            {error && (
              <div
                className="shadow1"
                style={{
                  marginTop: 12,
                  color: "#ff8178",
                  fontSize: 14,
                  lineHeight: "18px",
                  fontWeight: 700,
                }}
              >
                {error}
              </div>
            )}
          </>
        )}

        {/* =========================================
            STEP 2 - OTP
            ========================================= */}

        {step === "code" && (
          <>
            <div
              className="shadow2"
              style={{
                color: "#fff",
                fontSize: 22,
                lineHeight: "27px",
                fontWeight: 900,
                marginBottom: 8,
              }}
            >
              Check Your Email
            </div>

            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 600,
                marginBottom: 15,
              }}
            >
              We sent a 6-digit verification code to

              <div
                style={{
                  color: "#ffe16a",
                  fontSize: 16,
                  fontWeight: 900,
                  marginTop: 4,
                }}
              >
                {maskedEmail}
              </div>
            </div>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              disabled={loading}
              maxLength={6}
              placeholder="000000"
              onChange={(e) => {
                const next = e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 6);

                setCode(next);

                if (error) {
                  setError("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  verifyCode();
                }
              }}
              style={{
                width: "calc(100% + 20px)",
                height: 62,
                boxSizing: "border-box",
                borderRadius: 7,
                border: "3px solid #5d2a13",
                background: "#2a1208",
                color: "#fff",
                fontSize: 30,
                lineHeight: "40px",
                fontWeight: 900,
                letterSpacing: 9,
                paddingLeft: 10,
                outline: "none",
                textAlign: "center",
                marginLeft: -10,
                marginRight: -10,
              }}
            />

            {error && (
              <div
                className="shadow1"
                style={{
                  marginTop: 12,
                  color: "#ff8178",
                  fontSize: 14,
                  lineHeight: "18px",
                  fontWeight: 700,
                }}
              >
                {error}
              </div>
            )}

          </>
        )}

        {/* =========================================
            STEP 3 - SUCCESS
            ========================================= */}

        {step === "success" && (
          <>
            <div
              className="shadow2"
              style={{
                color: "#72ee8c",
                fontSize: 54,
                lineHeight: "60px",
                fontWeight: 900,
                marginBottom: 7,
              }}
            >
              ✓
            </div>

            <div
              className="shadow2"
              style={{
                color: "#fff",
                fontSize: 22,
                lineHeight: "27px",
                fontWeight: 900,
                marginBottom: 8,
              }}
            >
              Email Verified!
            </div>

            <div
              className="info"
              style={{
                color: "#fff",
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 600,
              }}
            >
              Your email has been successfully verified.
            </div>

            {canReceiveReward && (
              <div
                className="info"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <img
                  src={rewardIcon}
                  alt=""
                  style={{
                    width: 34,
                    height: 34,
                    objectFit: "contain",
                  }}
                />

                <span
                  className="shadow2"
                  style={{
                    color: "#ffe16a",
                    fontSize: 22,
                    fontWeight: 900,
                  }}
                >
                  +{rewardAmount} {rewardLabel}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* =========================================
          FOOTER - AGORA FORA DO modal_inside
          MAS DENTRO DO modal_body
          ========================================= */}

      <div className="modal_footer">
        {step === "email" && (
          <>
            <button
              type="button"
              className="game-button green-button"
              disabled={loading}
              onClick={sendCode}
              style={{
                marginRight: 10,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "SENDING..." : "SEND CODE"}
            </button>

            <button
              type="button"
              className="game-button red-button"
              disabled={loading}
              onClick={onClose}
              style={{
                opacity: loading ? 0.6 : 1,
              }}
            >
              CLOSE
            </button>
          </>
        )}

        {step === "code" && (
          <>
            <button
              type="button"
              className="game-button green-button"
              disabled={
                loading ||
                code.length !== 6
              }
              onClick={verifyCode}
              style={{
                marginRight: 10,
                opacity:
                  loading ||
                  code.length !== 6
                    ? 0.55
                    : 1,
              }}
            >
              {loading
                ? "VERIFYING..."
                : "VERIFY"}
            </button>

            <button
              type="button"
              className="game-button red-button"
              disabled={loading}
              onClick={onClose}
              style={{
                opacity: loading ? 0.55 : 1,
              }}
            >
              CLOSE
            </button>
          </>
        )}

        {step === "success" && (
          <button
            type="button"
            className="game-button green-button"
            onClick={onClose}
          >
            DONE
          </button>
        )}
      </div>
    </motion.div>
  </div>
);
}